/**
 * usePlaybookFavorites - localStorage persistence for favorite playbook entries
 *
 * Provides:
 * - toggleFavorite: Add/remove an entry from favorites
 * - isFavorite: Check if an entry is favorited
 * - favorites: Set of favorite entry IDs
 * - clearFavorites: Clear all favorites
 */

import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'playbook_favorites';

/**
 * Load favorites from localStorage
 */
function loadFavorites(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(parsed);
      }
    }
  } catch (error) {
    console.error('Failed to load playbook favorites:', error);
  }
  return new Set();
}

/**
 * Save favorites to localStorage
 */
function saveFavorites(favorites: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...favorites]));
  } catch (error) {
    console.error('Failed to save playbook favorites:', error);
  }
}

export function usePlaybookFavorites() {
  const [favorites, setFavorites] = useState<Set<string>>(() => loadFavorites());

  // Sync to localStorage when favorites change
  useEffect(() => {
    saveFavorites(favorites);
  }, [favorites]);

  /**
   * Toggle an entry's favorite status
   */
  const toggleFavorite = useCallback((entryId: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  }, []);

  /**
   * Check if an entry is favorited
   */
  const isFavorite = useCallback(
    (entryId: string) => favorites.has(entryId),
    [favorites]
  );

  /**
   * Clear all favorites
   */
  const clearFavorites = useCallback(() => {
    setFavorites(new Set());
  }, []);

  /**
   * Add multiple entries to favorites
   */
  const addMultiple = useCallback((entryIds: string[]) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      entryIds.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  /**
   * Remove multiple entries from favorites
   */
  const removeMultiple = useCallback((entryIds: string[]) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      entryIds.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  return {
    favorites,
    toggleFavorite,
    isFavorite,
    clearFavorites,
    addMultiple,
    removeMultiple,
  };
}
